/**
 * RouteErrorBoundary
 *
 * Error boundary for route-level components (especially lazy-loaded pages).
 * Handles two key failure modes:
 *
 * 1. Chunk load failures (stale asset after deploy):
 *    - Detects dynamic import / chunk load errors
 *    - Forces a single page refresh to fetch fresh assets
 *    - Uses sessionStorage flag to prevent infinite reload loops
 *
 * 2. Generic render errors:
 *    - Displays a recovery UI with retry and navigation options
 *    - Never shows a blank page
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { capturePortalException } from '@/lib/sentry';

const RELOAD_FLAG_KEY = 'coordi_chunk_reload';

function isChunkLoadError(error: Error): boolean {
  const message = error.message || '';
  const name = error.name || '';
  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Route name for logging (e.g. "Transaction Launch") */
  routeName?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  public state: RouteErrorBoundaryState = {
    hasError: false,
    error: null,
    isChunkError: false,
  };

  public static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const routeLabel = this.props.routeName || 'unknown route';
    console.error(`[RouteErrorBoundary] Error in ${routeLabel}:`, error, errorInfo);

    // For chunk load errors, attempt a single forced refresh
    if (isChunkLoadError(error)) {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY);
      if (!alreadyReloaded) {
        console.warn('[RouteErrorBoundary] Stale chunk detected, reloading page once');
        sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
        window.location.reload();
        return;
      }
      // If we already reloaded, fall through to show error UI
      console.warn('[RouteErrorBoundary] Already reloaded once for stale chunks, showing error UI');
    }

    capturePortalException(error, { componentStack: errorInfo.componentStack, boundary: 'route', routeName: routeLabel });
  }

  private handleRetry = (): void => {
    // Clear the reload flag so a fresh retry can attempt one more reload
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  private handleForceRefresh = (): void => {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    window.location.reload();
  };

  private handleGoHome = (): void => {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    window.location.href = '/dashboard';
  };

  public render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const routeLabel = this.props.routeName || 'this page';

    if (this.state.isChunkError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-6">
          <Alert className="max-w-lg border-amber-500/50 bg-amber-950/20">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Page update available</AlertTitle>
            <AlertDescription className="mt-2 space-y-3">
              <p>
                A newer version of Coordi is available. The page could not load
                because cached assets are out of date.
              </p>
              <div className="flex gap-2">
                <Button onClick={this.handleForceRefresh} variant="default" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Page
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" size="sm">
                  <Home className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-6">
        <Alert className="max-w-lg border-red-500/50 bg-red-950/20">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load {routeLabel}</AlertTitle>
          <AlertDescription className="mt-2 space-y-3">
            <p>
              An unexpected error occurred. You can try again or return to the dashboard.
            </p>
            {this.state.error && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Technical details
                </summary>
                <pre className="mt-2 text-xs bg-slate-900 p-2 rounded overflow-auto max-h-32">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <Button onClick={this.handleRetry} variant="default" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button onClick={this.handleGoHome} variant="outline" size="sm">
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
}
